'use client'

import * as React from 'react'
import Image from 'next/image'
import { ChevronDown, ArrowRight } from 'lucide-react'
import { useTranslations, useLocale } from 'next-intl'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { StaggeredMenu } from '@/components/ui/StaggeredMenu'
import BorderGlow from '@/components/ui/BorderGlow'
import { Link } from '@/i18n/navigation'
import { LanguageSwitcher } from '@/components/layout/language-switcher'

type DropdownItem = {
  label: string
  href: string | null
  description: string
  image?: string
}

export type NavLink =
  | { href: string; label: string; dropdown?: never }
  | { href?: never; label: string; dropdown: DropdownItem[] }

export interface NavProps {
  links?: NavLink[]
  cta?: { href: string; label?: string }
  logoHref?: string
  className?: string
  /** Minimal landing-page header: logo + single CTA only, no nav links or menu. */
  minimal?: boolean
}

export function Nav({ links, cta, logoHref = '/', className, minimal = false }: NavProps) {
  const t = useTranslations('nav')
  const tMenu = useTranslations('staggeredMenu')
  const locale = useLocale()
  const withLocale = (href: string) =>
    href.startsWith('/') && !href.startsWith('//') && !href.startsWith('/' + locale + '/') && href !== '/' + locale
      ? `/${locale}${href === '/' ? '' : href}`
      : href
  const [scrolled, setScrolled] = React.useState(false)
  const [dropdownOpen, setDropdownOpen] = React.useState(false)

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  React.useEffect(() => {
    if (!dropdownOpen) return
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Element
      if (!target.closest('header')) setDropdownOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [dropdownOpen])

  // Minimal landing-page header: logo + in-page anchor links + single CTA
  if (minimal) {
    const minimalCtaLabel = cta?.label ?? t('cta.label')
    const minimalLinks = (links ?? []).filter(
      (l): l is Extract<NavLink, { href: string }> => 'href' in l && !!l.href,
    )
    return (
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-all',
          scrolled ? 'bg-white/90 backdrop-blur border-b border-border' : 'bg-transparent',
          className,
        )}
      >
        <div className="mx-auto flex h-20 w-full max-w-container items-center justify-between px-6 md:px-12">
          <Link href={logoHref} className="flex items-center gap-2" aria-label={t('logoAriaLabel')}>
            <Image
              src="/infiteq-logo-large.png"
              alt="Infiteq"
              width={267}
              height={70}
              priority
              fetchPriority="high"
              sizes="267px"
              className="h-[35px] w-auto"
            />
          </Link>

          {minimalLinks.length > 0 && (
            <nav className="hidden items-center gap-8 md:flex">
              {minimalLinks.map((l) => (
                <Link
                  key={l.href}
                  href={l.href}
                  className="text-sm font-medium text-text-muted transition-colors hover:text-text-primary"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          )}

          {cta && (
            <Button asChild size="sm">
              <Link href={cta.href}>{minimalCtaLabel}</Link>
            </Button>
          )}
        </div>
      </header>
    )
  }

  // Derive nav links from translations if not provided via props
  // Order: Home, Web Dev, Mobile Apps, AI Integration, Solutions (dropdown), Partner With Us
  const resolvedLinks: NavLink[] = links ?? (() => {
    const translatedLinks = t.raw('links') as Array<{ label: string; href: string }>
    const solutionsData = t.raw('solutions') as {
      label: string
      items: Array<{ label: string; description: string; href: string | null; image: string }>
    }
    const navLinks: NavLink[] = [
      ...translatedLinks.slice(0, 4), // Home, Web Dev, Mobile Apps, AI Integration
      { label: solutionsData.label, dropdown: solutionsData.items } as NavLink,
      translatedLinks[4], // Partner With Us
    ]
    return navLinks
  })()

  const solutions = (t.raw('solutions') as {
    label: string
    ourProducts: string
    viewAll: string
    viewAllHref: string
    items: Array<{ label: string; description: string; href: string | null; image: string }>
  })

  const mobileItemsRaw = t.raw('mobileItems') as Array<{ label: string; link: string }>
  const mobileItems = mobileItemsRaw.map((it) => ({ ...it, link: withLocale(it.link) }))

  const ctaLabel = cta?.label ?? t('cta.label')

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-all',
          scrolled ? 'bg-white/90 backdrop-blur border-b border-border' : 'bg-transparent',
          className,
        )}
      >
        <div className="mx-auto flex h-20 w-full max-w-container items-center justify-between px-6 md:px-12">
          <Link href={logoHref} className="flex items-center gap-2" aria-label={t('logoAriaLabel')}>
            <Image
              src="/infiteq-logo-large.png"
              alt="Infiteq"
              width={267}
              height={70}
              priority
              fetchPriority="high"
              sizes="267px"
              className="h-[35px] w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-8 min-[1200px]:flex">
            {resolvedLinks.map((l) =>
              l.dropdown ? (
                <div key={l.label} className="relative">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium text-text-muted transition-colors hover:text-text-primary"
                    onClick={() => setDropdownOpen((v) => !v)}
                    aria-expanded={dropdownOpen}
                  >
                    {l.label}
                    <ChevronDown
                      size={14}
                      className={cn('transition-transform', dropdownOpen && 'rotate-180')}
                    />
                  </button>
                </div>
              ) : (
                <Link
                  key={l.href}
                  href={l.href}
                  className="text-sm font-medium text-text-muted transition-colors hover:text-text-primary"
                >
                  {l.label}
                </Link>
              ),
            )}
          </nav>

          <div className="hidden items-center gap-4 min-[1200px]:flex">
            <LanguageSwitcher />
            {cta && (
              <Button asChild size="sm">
                <Link href={cta.href}>{ctaLabel}</Link>
              </Button>
            )}
          </div>

          {/* Mobile: language switcher to the left of the StaggeredMenu hamburger */}
          <div className="ml-auto mr-16 flex items-center min-[1200px]:hidden sm:mr-20">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Solutions mega-dropdown */}
        {dropdownOpen && (
          <div className="hidden min-[1200px]:block border-t border-border bg-white/60 backdrop-blur-xl shadow-lg">
            <div className="mx-auto w-full max-w-container px-6 py-8 md:px-12">
              <div className="mb-6 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-text-subtle">
                  {solutions.ourProducts}
                </p>
                <Link href={solutions.viewAllHref} className="text-xs font-semibold text-blue hover:underline">
                  {solutions.viewAll}
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                {solutions.items.map((s) => {
                  const cardContent = (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-text-primary">{s.label}</span>
                        {s.href && <ArrowRight size={14} className="text-text-subtle transition-transform group-hover:translate-x-0.5" />}
                      </div>
                      <p className="text-xs text-text-muted leading-relaxed">{s.description}</p>
                      {s.image && (
                        <Image
                          src={s.image}
                          alt={s.label}
                          width={300}
                          height={160}
                          className="mt-2 w-full rounded-lg object-cover"
                        />
                      )}
                    </>
                  )
                  const inner = s.href ? (
                    <Link
                      href={s.href}
                      target="_blank"
                      rel="noopener"
                      onClick={() => setDropdownOpen(false)}
                      className="group flex flex-col gap-2 p-5 h-full"
                    >
                      {cardContent}
                    </Link>
                  ) : (
                    <div className="group flex flex-col gap-2 p-5 h-full opacity-60 cursor-default">
                      {cardContent}
                    </div>
                  )
                  return (
                    <BorderGlow
                      key={s.label}
                      backgroundColor="#ffffff"
                      borderRadius={12}
                      glowColor="220 80 70"
                      glowIntensity={1}
                      coneSpread={25}
                      colors={['rgba(43,90,255,0.15)', 'rgba(79,53,214,0.1)', 'rgba(124,47,232,0.1)']}
                    >
                      {inner}
                    </BorderGlow>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Mobile StaggeredMenu — only on small screens */}
      <div className="min-[1200px]:hidden">
        <StaggeredMenu
          items={mobileItems}
          colors={['#7C2FE8', '#4F35D6', '#2B5AFF', '#1A43CC']}
          accentColor="#2B5AFF"
          ctaHref={withLocale(cta?.href ?? '#contact')}
          ctaLabel={ctaLabel}
          openLabel={tMenu('open')}
          closeLabel={tMenu('close')}
          displayItemNumbering={false}
        />
      </div>
    </>
  )
}
